// Wholesale inquiry: quantity controls, live summary, validation and WhatsApp hand-off.
class WholesaleInquiry extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;

    const stringsEl = this.querySelector('[data-wholesale-strings]');
    this.strings = stringsEl ? JSON.parse(stringsEl.textContent) : { groups: {} };
    this.items = [...this.querySelectorAll('[data-wholesale-item]')];
    this.summaryList = this.querySelector('[data-summary-list]');
    this.summaryEmpty = this.querySelector('[data-summary-empty]');
    this.summaryTotal = this.querySelector('[data-summary-total]');
    this.formError = this.querySelector('[data-form-error]');
    this.status = this.querySelector('[data-status]');

    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.addEventListener('input', (event) => this.onInput(event), { signal });
    this.addEventListener('change', (event) => this.onChange(event), { signal });

    this.update();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  onClick(event) {
    const stepButton = event.target.closest('[data-step]');
    if (stepButton) {
      const item = stepButton.closest('[data-wholesale-item]');
      if (!item) return;
      const input = item.querySelector('[data-qty]');
      const next = this.toQty(input.value) + Number(stepButton.dataset.step);
      input.value = String(Math.max(0, next));
      this.update();
      return;
    }
    if (event.target.closest('[data-send]')) {
      event.preventDefault();
      this.send();
      return;
    }
    if (event.target.closest('[data-reset]')) {
      event.preventDefault();
      this.reset();
    }
  }

  onInput(event) {
    const target = event.target;
    if (target.matches('[data-qty]')) {
      // Keep digits only so pasted or typed values stay whole numbers.
      const digits = target.value.replace(/[^\d]/g, '');
      if (digits !== target.value) target.value = digits;
      this.update();
      return;
    }
    if (target.matches('[data-field]')) {
      if (target.dataset.field === 'phone') {
        const cleaned = target.value.replace(/[^\d+\s-]/g, '');
        if (cleaned !== target.value) target.value = cleaned;
      }
      if (target.getAttribute('aria-invalid') === 'true') this.validateField(target.dataset.field);
      this.update();
    }
  }

  onChange(event) {
    if (event.target.matches('[data-qty]')) {
      event.target.value = String(this.toQty(event.target.value));
      this.update();
    }
  }

  toQty(value) {
    const n = parseInt(String(value).replace(/[^\d]/g, ''), 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  field(key) {
    return this.querySelector(`[data-field="${key}"]`);
  }

  fieldValue(key) {
    return (this.field(key)?.value || '').trim();
  }

  // Accepts 10 digits, or with a leading 0 / 91 / +91; returns the 10-digit mobile number or null.
  normalisePhone(raw) {
    let digits = raw.replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
    if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
    return /^[6-9]\d{9}$/.test(digits) ? digits : null;
  }

  format(template, values) {
    return Object.entries(values).reduce((out, [key, value]) => out.replaceAll(`%${key}%`, String(value)), template || '');
  }

  collect() {
    const groups = new Map();
    let totalUnits = 0;

    for (const item of this.items) {
      const input = item.querySelector('[data-qty]');
      const qty = this.toQty(input.value);
      const units = Number(item.dataset.units) || 0;
      const packTotal = item.querySelector('[data-pack-total]');

      item.toggleAttribute('data-selected', qty > 0);
      if (packTotal) packTotal.textContent = this.format(this.strings.packCardTotal, { TOTAL: qty * units });

      if (qty <= 0) continue;

      const key = item.dataset.group || 'other';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({ name: item.dataset.name, qty, units, total: units > 0 ? qty * units : qty });
      totalUnits += units > 0 ? qty * units : qty;
    }

    return { groups, totalUnits };
  }

  lineFor(entry) {
    if (entry.units > 0) {
      return this.format(this.strings.packLine, { PACKS: entry.qty, UNITS: entry.units, TOTAL: entry.total });
    }
    return String(entry.qty);
  }

  update() {
    const { groups, totalUnits } = this.collect();
    if (!this.summaryList) return;

    this.summaryList.replaceChildren();
    for (const [key, entries] of groups) {
      const section = document.createElement('div');
      section.className = 'flex flex-col gap-1.5 border-b border-border pb-3 last:border-b-0';

      const heading = document.createElement('p');
      heading.className = 'text-xs font-bold uppercase tracking-wider text-on-surface-secondary';
      heading.textContent = this.strings.groups[key] || key;
      section.append(heading);

      const list = document.createElement('ul');
      list.className = 'flex flex-col gap-1';
      for (const entry of entries) {
        const row = document.createElement('li');
        row.className = 'flex items-baseline justify-between gap-3 text-sm';
        const name = document.createElement('span');
        name.textContent = entry.name;
        const qty = document.createElement('span');
        qty.className = 'shrink-0 font-semibold tabular-nums';
        qty.textContent = this.lineFor(entry);
        row.append(name, qty);
        list.append(row);
      }
      section.append(list);

      if (key === 'switches') {
        const total = entries.reduce((sum, entry) => sum + entry.total, 0);
        const totalRow = document.createElement('p');
        totalRow.className = 'flex justify-between text-sm font-semibold text-(--wh-blue-ink)';
        totalRow.innerHTML = '<span></span><span class="tabular-nums"></span>';
        totalRow.children[0].textContent = this.strings.totalSwitches;
        totalRow.children[1].textContent = String(total);
        section.append(totalRow);
      }
      this.summaryList.append(section);
    }

    const notes = this.fieldValue('notes');
    if (notes) {
      const section = document.createElement('div');
      section.className = 'flex flex-col gap-1.5';
      const heading = document.createElement('p');
      heading.className = 'text-xs font-bold uppercase tracking-wider text-on-surface-secondary';
      heading.textContent = this.strings.notesHeading;
      const body = document.createElement('p');
      body.className = 'whitespace-pre-line text-sm';
      body.textContent = notes;
      section.append(heading, body);
      this.summaryList.append(section);
    }

    const hasItems = groups.size > 0;
    this.summaryEmpty?.toggleAttribute('hidden', hasItems);
    if (this.summaryTotal) {
      this.summaryTotal.toggleAttribute('hidden', !hasItems);
      const value = this.summaryTotal.querySelector('[data-summary-total-value]');
      if (value) value.textContent = String(totalUnits);
    }
    if (hasItems && this.formError?.dataset.reason === 'items') this.clearFormError();
  }

  setFieldError(key, message) {
    const input = this.field(key);
    const error = this.querySelector(`[data-error-for="${key}"]`);
    if (input) input.setAttribute('aria-invalid', message ? 'true' : 'false');
    if (error) {
      error.textContent = message || '';
      error.classList.toggle('hidden', !message);
    }
    return !message;
  }

  validateField(key) {
    if (key === 'name') return this.setFieldError('name', this.fieldValue('name') ? '' : this.strings.errorName);
    if (key === 'phone') return this.setFieldError('phone', this.normalisePhone(this.fieldValue('phone')) ? '' : this.strings.errorPhone);
    return true;
  }

  showFormError(message, reason) {
    if (!this.formError) return;
    this.formError.textContent = message;
    this.formError.dataset.reason = reason;
    this.formError.hidden = false;
  }

  clearFormError() {
    if (!this.formError) return;
    this.formError.textContent = '';
    this.formError.hidden = true;
    delete this.formError.dataset.reason;
  }

  buildMessage(groups, totalUnits) {
    const s = this.strings;
    const phone = this.normalisePhone(this.fieldValue('phone'));
    const lines = [`*${s.messageTitle}*`, '', `*${s.customerHeading}*`];
    lines.push(`${s.labelName}: ${this.fieldValue('name')}`);
    lines.push(`${s.labelPhone}: +91 ${phone}`);
    const business = this.fieldValue('business');
    if (business) lines.push(`${s.labelBusiness}: ${business}`);
    const type = this.fieldValue('type');
    if (type) lines.push(`${s.labelType}: ${type}`);

    for (const [key, entries] of groups) {
      lines.push('', `*${s.groups[key] || key}*`);
      for (const entry of entries) lines.push(`• ${entry.name} – ${this.lineFor(entry)}`);
      if (key === 'switches') {
        lines.push(`${s.totalSwitches}: ${entries.reduce((sum, entry) => sum + entry.total, 0)}`);
      } else if (entries.length > 1) {
        lines.push(`${s.groupSubtotal}: ${entries.reduce((sum, entry) => sum + entry.qty, 0)}`);
      }
    }

    lines.push('', `*${s.totalHeading}*`, `${s.totalUnits}: ${totalUnits} (${s.totalUnitsNote})`);

    const notes = this.fieldValue('notes');
    if (notes) lines.push('', `*${s.notesHeading}*`, notes);

    lines.push('', `_${s.colourNote}_`, '', s.closing);
    return lines.join('\n');
  }

  send() {
    this.clearFormError();
    const nameOk = this.validateField('name');
    const phoneOk = this.validateField('phone');
    const { groups, totalUnits } = this.collect();

    if (!nameOk || !phoneOk) {
      this.showFormError(this.strings.errorSummary, 'fields');
      const firstInvalid = this.field(!nameOk ? 'name' : 'phone');
      firstInvalid?.scrollIntoView({ behavior: this.motion(), block: 'center' });
      firstInvalid?.focus({ preventScroll: true });
      return;
    }

    if (groups.size === 0) {
      this.showFormError(this.strings.errorItems, 'items');
      this.items[0]?.scrollIntoView({ behavior: this.motion(), block: 'center' });
      return;
    }

    const phoneTarget = (this.dataset.phone || '').replace(/\D/g, '');
    if (!phoneTarget) return;

    const message = this.buildMessage(groups, totalUnits);
    const url = `https://wa.me/${phoneTarget}?text=${encodeURIComponent(message)}`;
    if (this.status) this.status.textContent = this.strings.opening;

    // Phones hand the link to the WhatsApp app; desktops open WhatsApp Web in a new tab.
    const isMobile = window.matchMedia('(pointer: coarse)').matches && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    if (isMobile) {
      window.location.href = url;
      return;
    }
    const tab = window.open(url, '_blank');
    if (tab) {
      tab.opener = null;
    } else {
      window.location.href = url;
    }
  }

  motion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  reset() {
    for (const item of this.items) {
      const input = item.querySelector('[data-qty]');
      if (input) input.value = '0';
    }
    for (const input of this.querySelectorAll('[data-field]')) {
      if (input.tagName === 'SELECT') {
        input.selectedIndex = 0;
      } else {
        input.value = '';
      }
      this.setFieldError(input.dataset.field, '');
    }
    this.clearFormError();
    this.update();
    if (this.status) this.status.textContent = this.strings.resetDone;
  }
}

if (!customElements.get('wholesale-inquiry')) customElements.define('wholesale-inquiry', WholesaleInquiry);
